/**
 * BrandingAdminPage — coach/admin self-service for tenant branding.
 *
 * Mounted at ``/coach/admin/branding``. A coach edits the brand for
 * their own JWT tenant; an admin can flip the target via the dropdown
 * to edit any registered tenant.
 *
 * Persists through PUT /scenario-os/branding/{client_id} which writes
 * to the same config_store doc (cfg:client:scenario_os:v1:tenant_branding)
 * that the seed script (`seed_tenant_branding.py`) populates. The
 * install pipeline's default merge mode preserves UI edits, so changes
 * made here survive a redeploy.
 *
 * Why this lives at the **Scenario OS tier** and not per-domain: a
 * coach who teaches multiple domains under the same tenant should see
 * one consistent brand across all of them. Domain-level flavour (the
 * sidebar icon, group label) lives in CoachLayout's DOMAIN_PRESENTATION
 * map — that's metadata, not brand identity.
 */

import React, { useEffect, useMemo, useState } from 'react';
import CoachLayout from './CoachLayout';
import { useAuth } from '../../context/AuthContext';
import { brandingAPI, tenantAPI } from '../../services/api';

const DEFAULT_BRAND = {
  display_name: '',
  primary_color: '#0f172a',
  accent_color: '#22d3ee',
  text_color: '#f8fafc',
  background_color: '#020617',
  logo_url: '',
  favicon_url: '',
  hero_tagline: '',
  show_powered_by: true,
  email: { from_name: '', from_address: '' },
};

export default function BrandingAdminPage() {
  const { tenantId, isAdmin } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [targetTenant, setTargetTenant] = useState('');
  const [draft, setDraft] = useState(DEFAULT_BRAND);
  const [original, setOriginal] = useState(DEFAULT_BRAND);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingMsg, setSavingMsg] = useState('');
  const [error, setError] = useState('');
  const [source, setSource] = useState('');

  // Load the tenant list once on mount. Coaches see at minimum their
  // own tenant; admins see every registered tenant.
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await tenantAPI.list();
        if (!mounted) return;
        const list = Array.isArray(data?.tenants) ? data.tenants : [];
        setTenants(list);
        const initial = (list.find((t) => t.is_caller_tenant) || list[0])?.client_id || tenantId;
        setTargetTenant(initial);
      } catch (e) {
        if (mounted) setError(e?.response?.data?.detail || e.message);
      }
    })();
    return () => { mounted = false; };
  }, [tenantId]);

  // Load branding for the selected tenant whenever the dropdown
  // changes.
  useEffect(() => {
    if (!targetTenant) return;
    let mounted = true;
    setLoading(true);
    (async () => {
      try {
        const { data } = await brandingAPI.getForClient(targetTenant);
        if (!mounted) return;
        const merged = {
          ...DEFAULT_BRAND,
          ...data,
          email: { ...DEFAULT_BRAND.email, ...(data.email || {}) },
        };
        setDraft(merged);
        setOriginal(merged);
        setSource(data.source || '');
      } catch (e) {
        if (mounted) setError(e?.response?.data?.detail || e.message);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [targetTenant]);

  const editableTenants = useMemo(
    () => tenants.filter((t) => t.editable_by_caller),
    [tenants],
  );
  const targetMeta = useMemo(
    () => tenants.find((t) => t.client_id === targetTenant) || null,
    [tenants, targetTenant],
  );
  const canEdit = !!targetMeta?.editable_by_caller;
  const dirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(original),
    [draft, original],
  );

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const setEmail = (k, v) => setDraft((d) => ({ ...d, email: { ...(d.email || {}), [k]: v } }));

  const handleSave = async () => {
    if (!canEdit) return;
    setSaving(true);
    setSavingMsg('');
    try {
      const payload = {
        display_name: draft.display_name,
        primary_color: draft.primary_color,
        accent_color: draft.accent_color,
        text_color: draft.text_color,
        background_color: draft.background_color,
        logo_url: draft.logo_url || null,
        favicon_url: draft.favicon_url || null,
        hero_tagline: draft.hero_tagline,
        show_powered_by: draft.show_powered_by,
        email: draft.email,
      };
      const { data } = await brandingAPI.updateForClient(targetTenant, payload);
      const merged = {
        ...DEFAULT_BRAND,
        ...data,
        email: { ...DEFAULT_BRAND.email, ...(data.email || {}) },
      };
      setDraft(merged);
      setOriginal(merged);
      setSavingMsg('Saved. Hard-refresh to see chrome update.');
    } catch (e) {
      setSavingMsg(`Save failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
      setTimeout(() => setSavingMsg(''), 6000);
    }
  };

  const handleReset = () => setDraft(original);

  if (loading) {
    return (
      <CoachLayout title="Branding">
        <div style={{ padding: 24 }}>Loading branding…</div>
      </CoachLayout>
    );
  }

  return (
    <CoachLayout title="Branding">
      <div style={{ padding: 24, maxWidth: 1100 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 18 }}>
          <h2 style={{ margin: 0 }}>Tenant Branding</h2>
          <span style={{ fontSize: 12, color: 'var(--coach-text-muted)' }}>
            tenant = <strong>{targetTenant || tenantId}</strong> ·
            source = <strong>{source}</strong> ·
            scope = TeachIQ (config_store)
          </span>
        </div>

        {error && (
          <div style={{ padding: 10, background: 'rgba(239,68,68,0.1)', color: 'var(--coach-danger)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 8, marginBottom: 12 }}>
            {error}
          </div>
        )}

        {/* Tenant selector — only useful for admins who can flip
            between tenants. Non-admins see a single option. */}
        <div style={{ marginBottom: 18 }}>
          <label style={labelStyle}>
            Tenant
            <select
              value={targetTenant}
              onChange={(e) => setTargetTenant(e.target.value)}
              style={inputStyle}
              disabled={!isAdmin && editableTenants.length <= 1}
            >
              {tenants.map((t) => (
                <option key={t.client_id} value={t.client_id}>
                  {t.display_name} ({t.client_id})
                  {t.is_caller_tenant ? ' — yours' : ''}
                  {!t.editable_by_caller ? ' [read-only]' : ''}
                </option>
              ))}
            </select>
          </label>
        </div>

        {!canEdit && (
          <div style={{ padding: 10, background: 'rgba(245,158,11,0.1)', color: 'var(--coach-warning)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: 8, marginBottom: 12 }}>
            You're viewing this tenant in read-only mode. To edit branding for a different
            tenant, log in as that tenant's coach or as a platform admin.
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          {/* ── Form ── */}
          <div>
            <FormField label="Display name" value={draft.display_name} onChange={(v) => set('display_name', v)} disabled={!canEdit} />
            <FormField label="Hero tagline" value={draft.hero_tagline} onChange={(v) => set('hero_tagline', v)} disabled={!canEdit} />
            <FormField label="Logo URL" value={draft.logo_url || ''} onChange={(v) => set('logo_url', v)} disabled={!canEdit}
                       hint="https URL to the tenant logo (PNG/SVG, ~120×40)" />
            <FormField label="Favicon URL" value={draft.favicon_url || ''} onChange={(v) => set('favicon_url', v)} disabled={!canEdit}
                       hint="https URL to a 32×32 PNG or ICO" />

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <ColorField label="Primary" value={draft.primary_color} onChange={(v) => set('primary_color', v)} disabled={!canEdit} />
              <ColorField label="Accent"  value={draft.accent_color}  onChange={(v) => set('accent_color', v)}  disabled={!canEdit} />
              <ColorField label="Text"    value={draft.text_color}    onChange={(v) => set('text_color', v)}    disabled={!canEdit} />
              <ColorField label="Background" value={draft.background_color} onChange={(v) => set('background_color', v)} disabled={!canEdit} />
            </div>

            <div style={{ marginTop: 12 }}>
              <FormField label="Email — from name" value={draft.email?.from_name || ''} onChange={(v) => setEmail('from_name', v)} disabled={!canEdit} />
              <FormField label="Email — from address" value={draft.email?.from_address || ''} onChange={(v) => setEmail('from_address', v)} disabled={!canEdit} />
            </div>

            <label style={{ ...labelStyle, display: 'flex', gap: 8, alignItems: 'center', marginTop: 14 }}>
              <input
                type="checkbox"
                checked={!!draft.show_powered_by}
                onChange={(e) => set('show_powered_by', e.target.checked)}
                disabled={!canEdit}
              />
              <span>Show "powered by Feenix" footer</span>
            </label>

            <div style={{ marginTop: 18, display: 'flex', gap: 8 }}>
              <button className="btn-primary" disabled={!canEdit || !dirty || saving} onClick={handleSave}>
                {saving ? 'Saving…' : 'Save branding'}
              </button>
              <button className="btn-ghost" disabled={!dirty || saving} onClick={handleReset}>
                Reset
              </button>
              {savingMsg && <span style={{ fontSize: 12, color: 'var(--coach-primary)', alignSelf: 'center' }}>{savingMsg}</span>}
            </div>
          </div>

          {/* ── Live preview ── */}
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Live preview</div>
            <BrandPreview brand={draft} />
            <div style={{ fontSize: 11.5, color: 'var(--coach-text-muted)', marginTop: 8 }}>
              Updates are persisted to <code>cfg:client:scenario_os:v1:tenant_branding</code>.
              The learner app fetches branding on mount, so existing
              sessions need a hard-refresh ({navigator.platform.includes('Mac') ? '⌘+Shift+R' : 'Ctrl+Shift+R'})
              to see the new chrome.
            </div>
          </div>
        </div>
      </div>
    </CoachLayout>
  );
}

function FormField({ label, value, onChange, disabled, hint }) {
  return (
    <label style={labelStyle}>
      {label}
      <input
        type="text"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        style={inputStyle}
      />
      {hint && <span style={{ fontSize: 11, color: 'var(--coach-text-muted)' }}>{hint}</span>}
    </label>
  );
}

function ColorField({ label, value, onChange, disabled }) {
  return (
    <label style={labelStyle}>
      {label}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <input
          type="color"
          value={value || '#000000'}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          style={{ width: 40, height: 30, padding: 0, border: '1px solid var(--coach-border)', borderRadius: 4 }}
        />
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          style={{ ...inputStyle, fontFamily: 'monospace' }}
        />
      </div>
    </label>
  );
}

function BrandPreview({ brand }) {
  return (
    <div
      style={{
        background: brand.background_color,
        color: brand.text_color,
        border: '1px solid var(--coach-border)',
        borderRadius: 12,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          background: brand.primary_color,
          padding: '14px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}
      >
        {brand.logo_url ? (
          <img src={brand.logo_url} alt="logo" style={{ height: 32, maxWidth: 120, objectFit: 'contain' }} />
        ) : (
          <span style={{ fontSize: 22 }}>🎓</span>
        )}
        <strong style={{ color: brand.text_color }}>{brand.display_name || 'Untitled tenant'}</strong>
      </div>
      <div style={{ padding: 18 }}>
        <div style={{ fontSize: 18, marginBottom: 8 }}>{brand.hero_tagline || 'Scenario-based learning, built on Feenix'}</div>
        <button
          style={{
            background: brand.accent_color,
            color: brand.text_color,
            border: 'none',
            padding: '8px 14px',
            borderRadius: 6,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Start a lesson
        </button>
        {brand.show_powered_by && (
          <div style={{ fontSize: 11, opacity: 0.7, marginTop: 14 }}>powered by Feenix</div>
        )}
      </div>
    </div>
  );
}

const labelStyle = {
  display: 'block',
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--coach-text)',
  marginBottom: 12,
};

const inputStyle = {
  display: 'block',
  width: '100%',
  marginTop: 4,
  padding: '8px 10px',
  border: '1px solid var(--coach-border)',
  borderRadius: 6,
  fontSize: 14,
  boxSizing: 'border-box',
  background: 'var(--coach-input-bg)',
  color: 'var(--coach-text)',
};
